import React from 'react';
import { Mail, PencilIcon, UserX } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface CompanyMember {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url?: string;
  companyRole: string;
}

interface CompanyMemberCardProps {
  member: CompanyMember;
  isAdmin: boolean;
  onEdit: (member: CompanyMember) => void;
  onRemove: (member: CompanyMember) => void;
}

const CompanyMemberCard: React.FC<CompanyMemberCardProps> = ({ 
  member, 
  isAdmin, 
  onEdit, 
  onRemove 
}) => {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <Avatar className="h-12 w-12 shrink-0">
            <AvatarImage src={member.avatar_url || ''} alt={`${member.first_name} ${member.last_name}`} />
            <AvatarFallback>
              {member.first_name && member.last_name 
                ? `${member.first_name[0]}${member.last_name[0]}`.toUpperCase() 
                : 'U'}
            </AvatarFallback>
          </Avatar>

          {/* Member Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="font-semibold text-base line-clamp-1">
                {member.first_name} {member.last_name}
              </h3>
              <Badge 
                variant={member.companyRole === 'admin' ? 'default' : 'secondary'}
                className="shrink-0"
              >
                {member.companyRole}
              </Badge>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
              <Mail className="h-4 w-4 shrink-0" />
              <a 
                href={`mailto:${member.email}`}
                className="hover:text-primary line-clamp-1"
              >
                {member.email}
              </a>
            </div>

            {/* Action Buttons - Only show if admin */}
            {isAdmin && (
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex-1"
                  onClick={() => onEdit(member)}
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex-1"
                  onClick={() => onRemove(member)}
                >
                  <UserX className="h-4 w-4 mr-1" />
                  Remove
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanyMemberCard;
